import { TanStackDevtools } from '@tanstack/react-devtools';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import {
  GlobalConfig as BeyondGlobalConfig,
  GlobalConfigContext as BeyondGlobalConfigContext,
  ReactToastifyToastLayer,
} from '@zeiss/beyond-online-react';
import {
  createConfig,
  GlobalConfigProvider,
  PageLoader,
} from '@zeiss/lib-components';
import { AppInsightsConfig, AppInsightsProvider } from '@zeiss/lib-helpers';
import { ReactRouterLinkComponent } from '@zeiss/lib-router';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider, AuthProviderProps, useAuth } from 'react-oidc-context';
import { RouterProvider } from 'react-router-dom';

import { AppConfigProvider } from 'src/config';
import { BASE_PATH, PRODUCT_TITLE } from 'src/constants';
import { GlobalEvaluationsProvider } from 'src/context/GlobalEvaluationsContext';
import { ENV } from 'src/env';
import {
  useAppConfig,
  useDocumentMetadata,
  useGlobalEvaluations,
} from 'src/hooks';
import { queryClient } from 'src/queryClient';
import { router } from 'src/router';

const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    auth: { authority, clientId },
    locationsApiUrl,
    logging: { connectionString } = {},
    supportedLocations,
  } = useAppConfig();

  const oidcConfig: AuthProviderProps = {
    authority,
    client_id: clientId,
    loadUserInfo: true,
    post_logout_redirect_uri: window.location.origin,
    redirect_uri: `${window.location.origin}${BASE_PATH}/callback`,
    revokeTokensOnSignout: true,
    scope: 'openid profile email idm-proxy:user:read',
  };

  const aiConfig: AppInsightsConfig = {
    connectionString,
    productName: PRODUCT_TITLE,
    productVersion: ENV.VITE_BUILD_VERSION,
  };

  return (
    <AppInsightsProvider config={aiConfig}>
      <AuthProvider {...oidcConfig}>
        <GlobalConfigProvider
          config={{
            locationsApiUrl,
            supportedLocations,
          }}
        >
          {children}
        </GlobalConfigProvider>
      </AuthProvider>
    </AppInsightsProvider>
  );
};

/**
 * Inner component that has access to EvaluationsContext and updates document
 * metadata based on authentication and authorization state.
 *
 * Also handles authentication error recovery and silent token renewal failures.
 * Placed above the router so these handlers stay mounted even when error
 * boundaries replace route components.
 */
const AppRouterWithMetadata = () => {
  const { events, signinRedirect } = useAuth();
  const { error } = useGlobalEvaluations();
  const signinTriggered = useRef(false);

  useDocumentMetadata();

  /**
   * Redirect to sign-in when the evaluations API returns 401/403.
   *
   * This handles the case where a user's session expires while they are
   * actively using the app. Background refetches (every 5 min or on window
   * focus) may receive 401/403, which would otherwise show a 404 page.
   *
   * Guarded by a ref to prevent multiple concurrent `signinRedirect` calls
   * (React Query may surface the same error on successive refetch ticks
   * while the redirect is still pending).
   */
  useEffect(() => {
    if (
      !signinTriggered.current &&
      error instanceof Response &&
      (error.status === 401 || error.status === 403)
    ) {
      signinTriggered.current = true;
      signinRedirect();
    }
  }, [error, signinRedirect]);

  /**
   * Handle silent token renewal failures.
   *
   * 'react-oidc-context' attempts to automatically renew the access token
   * one minute before it expires. When renewal fails (e.g., user interaction
   * is required), we trigger a full sign-in redirect.
   *
   * @see {@link https://github.com/authts/react-oidc-context#documentation}
   */
  useEffect(() => {
    return events.addSilentRenewError((error) => {
      console.error(error.message);
      signinRedirect();
    });
  }, [events, signinRedirect]);

  return <RouterProvider router={router} />;
};

const AppRouter = () => {
  const auth = useAuth();
  const { t } = useTranslation(['common']);

  /**
   * Wait for authentication to initialize. Using the `LoadingSpinner` exposes
   * that there is an app, but using `return null` could impact UX.
   */
  if (auth.isLoading) {
    return <PageLoader label={t('common:loadingFeedback.loading')} size="xl" />;
  }

  /**
   * Make evaluations context available. The provider itself only fetches
   * evaluations when authenticated.
   */
  return (
    <GlobalEvaluationsProvider>
      <AppRouterWithMetadata />
    </GlobalEvaluationsProvider>
  );
};

function App() {
  const beyondGlobalConfig = useMemo((): BeyondGlobalConfig => {
    return createConfig({
      global: {
        linkComponent: ReactRouterLinkComponent,
      },
    });
  }, []);

  // `TanStackDevtools` has throws `failed to mount` errors in test environments
  // eslint-disable-next-line no-restricted-syntax
  const showDevtools = import.meta.env.MODE === 'development';

  return (
    <BeyondGlobalConfigContext.Provider value={beyondGlobalConfig}>
      <AppConfigProvider>
        <AppProvider>
          <QueryClientProvider client={queryClient}>
            <ReactToastifyToastLayer />
            <AppRouter />

            {showDevtools && (
              <TanStackDevtools
                eventBusConfig={{
                  connectToServerBus: true,
                }}
                plugins={[
                  {
                    name: 'TanStack Query',
                    render: <ReactQueryDevtoolsPanel />,
                  },
                  {
                    name: 'TanStack Form',
                    render: <FormDevtoolsPanel />,
                  },
                ]}
              />
            )}
          </QueryClientProvider>
        </AppProvider>
      </AppConfigProvider>
    </BeyondGlobalConfigContext.Provider>
  );
}

export default App;
